/**
 * What-If Control Bar — the persistent instrument at the bottom of the
 * canvas (design doc executive-view-design/EXEC_WHATIF_v1_design.md §3,
 * D10). Always mounted, always editable; it IS the "new analysis"
 * control — there is no separate "Start a new what-if" button.
 *
 * Layout (§3, revised 2026-08-19 for vertical economy; revised again
 * 2026-08-21 after the adversarial UI review): a two-row band. Left =
 * two stacked outlined Selects with floating labels ("What happens to" /
 * "if we change"). Right of the stack = the "In this way" free-text
 * field, EXACTLY the stack's height — top-aligned with the first Select
 * and bottom-aligned with the second (deterministic px, not flex
 * stretch, which failed to resolve in practice; 2026-08-21 feedback).
 * Far right = the action cluster.
 *
 * ACTION CLUSTER (2026-08-21 UI review; revised 2026-08-22 mark 4;
 * revised again 2026-08-23): ALL run-lifecycle buttons live here — the
 * canvas never renders its own competing actions. The cluster is a
 * VERTICAL stack mirroring the dropdown stack's geometry (SELECT_HEIGHT
 * buttons, STACK_GAP gap), so the primary action always occupies the
 * same bottom-right slot and the panel reads as static across states:
 * resting = Run analysis alone; proposal pending (`proposalActions`
 * present) = Discard stacked over Approve. Run analysis is HIDDEN while
 * a proposal is pending (one primary CTA at a time; three buttons don't
 * fit at the container width) — re-running a changed ask is
 * Discard-then-Run; D4's supersede-on-run stays backend-supported for
 * the recents/reopen paths.
 *
 * This component is presentation + local-value plumbing only: WhatIfView
 * owns the actual form state, the home→confirm transition rule, and the
 * approve/discard handlers.
 */
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
// This app remaps MUI's generated class names (src/MuiClassNameSetup.ts:
// "Mui" -> "Trinity"), so a hardcoded '& .MuiInputBase-root' selector
// matches NOTHING at runtime — the exported class constants track the
// real generated names (same pattern as theme.ts's inputLabelClasses).
// This dead-selector trap is why the earlier alignItems:'stretch' fix
// "mysteriously" failed: the root stretched, but the visible input child
// was addressed through a selector that never matched.
import { inputBaseClasses } from '@mui/material/InputBase';

import { EXEC_CONTENT_CONTAINER_SX } from '../layout';
import { WhatIfOptionItem } from '../types';

// Deterministic band geometry: two size="small" outlined Selects (40px
// each) + the stack gap. The text field is pinned to the same total so
// its bottom edge lands exactly on the second Select's bottom edge
// (2026-08-21 feedback — flex `alignItems: stretch` left the field
// shorter than the stack in practice). Widening the gap 8px → 12px also
// delivers the requested "very slightly taller" band.
const SELECT_HEIGHT = 40;
const STACK_GAP = 12;
const BAND_CONTENT_HEIGHT = SELECT_HEIGHT * 2 + STACK_GAP;

// Approve/Discard wiring, present only while a proposal is pending
// (WhatIfView passes null in every other canvas state).
export interface ProposalBarActions {
  canApprove: boolean;
  approving: boolean;
  discarding: boolean;
  onApprove: () => void;
  onDiscard: () => void;
}

interface ControlBarProps {
  outputs: WhatIfOptionItem[];
  inputs: WhatIfOptionItem[];
  outputId: number | '';
  inputId: number | '';
  changeText: string;
  onOutputChange: (outputId: number) => void;
  onInputChange: (inputId: number) => void;
  onChangeTextChange: (changeText: string) => void;
  onRun: () => void;
  running: boolean;
  proposalActions?: ProposalBarActions | null;
}

const ControlBar: React.FC<ControlBarProps> = ({
  outputs,
  inputs,
  outputId,
  inputId,
  changeText,
  onOutputChange,
  onInputChange,
  onChangeTextChange,
  onRun,
  running,
  proposalActions = null,
}) => {
  const canRun = outputId !== '' && inputId !== '' && changeText.trim() !== '';
  const actionBusy =
    !!proposalActions &&
    (proposalActions.approving || proposalActions.discarding);

  return (
    // Two nested Boxes on purpose: the OUTER one carries the band's
    // border + background full-bleed across the pane, while the INNER
    // row takes EXEC_CONTENT_CONTAINER_SX so the controls line up with
    // the canvas above them (and with every other executive screen).
    // Previously the bar was a bare sibling with only `px: 4`, so it
    // spanned the full pane while the canvas stayed centered at 1100 —
    // the mismatch was visible as the bar's controls sitting left of the
    // content they belong to (2026-08-21 UI review).
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        // Subtle panel tint (2026-08-22 mark 3): differentiates the
        // instrument band from the white canvas; the controls keep
        // paper backgrounds so they read as instruments ON the panel.
        bgcolor: 'grey.50',
      }}
    >
      <Box
        sx={{
          ...EXEC_CONTENT_CONTAINER_SX,
          py: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Left: the two-row dropdown stack. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${STACK_GAP}px`,
            minWidth: 280,
            flex: '1 1 280px',
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel id="whatif-output-label">What happens to</InputLabel>
            <Select
              labelId="whatif-output-label"
              label="What happens to"
              sx={{ bgcolor: 'background.paper' }}
              value={outputId}
              onChange={(e: SelectChangeEvent<number | ''>) =>
                onOutputChange(e.target.value as number)
              }
            >
              {outputs.map((output) => (
                <MenuItem key={output.id} value={output.id}>
                  {output.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="whatif-input-label">if we change</InputLabel>
            <Select
              labelId="whatif-input-label"
              label="if we change"
              sx={{ bgcolor: 'background.paper' }}
              value={inputId}
              onChange={(e: SelectChangeEvent<number | ''>) =>
                onInputChange(e.target.value as number)
              }
            >
              {inputs.map((input) => (
                <MenuItem key={input.id} value={input.id}>
                  {input.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right of the stack: the free-text instruction, pinned to the
          stack's exact height (BAND_CONTENT_HEIGHT — see the constant's
          comment); long text scrolls inside the box rather than growing
          it, so the bar never eats into the canvas's scarce vertical
          space. */}
        <TextField
          label="In this way"
          placeholder="e.g. increases 10% in the US and Germany from 2027"
          multiline
          rows={2}
          value={changeText}
          onChange={(e) => onChangeTextChange(e.target.value)}
          sx={{
            flex: '2 1 360px',
            minWidth: 280,
            height: BAND_CONTENT_HEIGHT,
            [`& .${inputBaseClasses.root}`]: {
              height: '100%',
              alignItems: 'flex-start',
              bgcolor: 'background.paper',
            },
            [`& .${inputBaseClasses.inputMultiline}`]: {
              height: '100% !important',
            },
          }}
        />

        {/* Far right: the action cluster (see file header) — a vertical
          stack that mirrors the dropdown stack's geometry (2026-08-23
          feedback: stack the two buttons; the primary always occupies
          the same bottom-right slot, so the panel reads as static
          across states). Buttons match SELECT_HEIGHT so the column is a
          true twin of the Selects: pre-flight = Run analysis alone in
          the bottom slot; proposal pending = Discard on top, Approve in
          the bottom slot. The "Nothing is applied until you approve"
          caption was removed here (same feedback) — the headline's
          "proposed changes" copy and the Approve gate itself carry that
          meaning. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-end',
            gap: `${STACK_GAP}px`,
            height: BAND_CONTENT_HEIGHT,
            flex: '0 0 auto',
            minWidth: 140,
          }}
        >
          {proposalActions ? (
            <>
              <Button
                size="small"
                variant="outlined"
                sx={{ height: SELECT_HEIGHT }}
                disabled={actionBusy}
                onClick={proposalActions.onDiscard}
              >
                {proposalActions.discarding ? 'Discarding…' : 'Discard'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={{ height: SELECT_HEIGHT }}
                disabled={actionBusy || !proposalActions.canApprove}
                onClick={proposalActions.onApprove}
                startIcon={
                  proposalActions.approving ? (
                    <CircularProgress size={14} />
                  ) : undefined
                }
              >
                {proposalActions.approving ? 'Approving…' : 'Approve'}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ height: SELECT_HEIGHT }}
              disabled={!canRun || running}
              onClick={onRun}
            >
              Run analysis
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ControlBar;
